/**
 * ChangeJourney — a simple status stepper for a proposed change's path to
 * production: Saved → Checks passed → Publishing → Live in production.
 *
 * The console's FleetDetail carries a richer ChangeJourney/friendlyCheckFailure
 * pair (PR #157) that isn't present in this checkout, so this is a minimal
 * reconstruction driven by the same two inputs it would use: the PR's CI
 * checks state, and the result of an Approve & Deploy action (once run).
 *
 * ADR-110 Layer B: "Live in production" for an already-merged PR reflects the
 * ACTUAL Deploy Fleet workflow-run result (`deploy`), not just that the PR
 * merged to main — see buildSteps/deployLiveState in
 * ../utils/changeJourneySteps (kept in a separate non-component file to avoid
 * a mixed component+helper export from this file).
 */
import type { DeployInfo, ImprovementPRChecks } from '../api/client'
import { buildSteps } from '../utils/changeJourneySteps'
import type { ApproveOutcome, StepState } from '../utils/changeJourneySteps'

export type { ApproveOutcome } from '../utils/changeJourneySteps'

function stepClass(state: StepState): string {
  switch (state) {
    case 'done':
      return 'bg-green-100 text-green-800 border-green-300'
    case 'active':
      return 'bg-amber-100 text-amber-800 border-amber-300 animate-pulse'
    case 'failed':
      return 'bg-red-100 text-red-800 border-red-300'
    default:
      return 'bg-gray-100 text-gray-400 border-gray-200'
  }
}

function stepIcon(state: StepState): string {
  switch (state) {
    case 'done': return '✓'
    case 'active': return '…'
    case 'failed': return '✗'
    default: return '○'
  }
}

export function ChangeJourney({
  checks,
  approve,
  merged,
  deploy,
}: {
  checks: ImprovementPRChecks
  approve?: ApproveOutcome
  merged?: boolean
  deploy?: DeployInfo | null
}) {
  const steps = buildSteps(checks, approve, merged, deploy)
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {steps.map((s, i) => (
        <div key={s.label} className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${stepClass(s.state)}`}>
            <span>{stepIcon(s.state)}</span>
            {s.label}
          </span>
          {i < steps.length - 1 && <span className="text-gray-300">→</span>}
        </div>
      ))}
    </div>
  )
}
